import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { productsStorage } from '../../services/productsStorage';
import { productsService } from '../../services/productsService';
import { Input } from '@chapche/ui';

type ProductCodeAutocompleteProps = {
    value: string;
    onChange: (productCode: string) => void;
    hasError?: boolean;
};

type ProductOption = {
    productCode: string;
    title: string;
};

const ProductCodeAutocomplete: React.FC<ProductCodeAutocompleteProps> = ({
    value,
    onChange,
    hasError = false,
}) => {
    const { i18n } = useTranslation();
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const loadingRef = useRef(false);
    const [query, setQuery] = useState(value || '');
    const [open, setOpen] = useState(false);
    const [loaded, setLoaded] = useState(
        ((productsStorage as { productsList?: unknown[] }).productsList || []).length > 0,
    );

    useEffect(() => {
        setQuery(value || '');
    }, [value]);

    const ensureProductsLoaded = () => {
        if (loaded || loadingRef.current) {
            return;
        }
        if (((productsStorage as { productsList?: unknown[] }).productsList || []).length > 0) {
            setLoaded(true);
            return;
        }
        loadingRef.current = true;
        productsStorage.loadItems(() => {
            loadingRef.current = false;
            setLoaded(true);
        });
    };

    useEffect(() => {
        const onMouseDown = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, []);

    const options: ProductOption[] = useMemo(() => {
        const products = (productsStorage as { productsList?: Array<{ productCode: string; isDeleted?: boolean }> }).productsList || [];
        const normalized = query.trim().toLowerCase();
        return products
            .filter((product) => !product.isDeleted)
            .map((product) => ({
                productCode: product.productCode,
                title: productsService.getLocalization(
                    i18n.resolvedLanguage,
                    product.productCode,
                    true,
                    i18n,
                ) || product.productCode,
            }))
            .filter((option) => {
                if (!normalized) {
                    return true;
                }
                return option.productCode.toLowerCase().includes(normalized)
                    || option.title.toLowerCase().includes(normalized);
            })
            .slice(0, 12);
    }, [query, loaded, i18n, i18n.resolvedLanguage]);

    return (
        <div className="cooking-steps__autocomplete" ref={wrapperRef}>
            <Input
                fullWidth
                label={i18n.t('app.page.cookingStep.productCode')}
                state={hasError ? 'error' : 'default'}
                value={query}
                onChange={(event) => {
                    setQuery(event.target.value);
                    onChange(event.target.value);
                    ensureProductsLoaded();
                    setOpen(true);
                }}
                onFocus={() => {
                    ensureProductsLoaded();
                    setOpen(true);
                }}
                placeholder={i18n.t('app.page.cookingStep.productCodePlaceholder')}
                autoComplete="off"
            />
            {open && options.length > 0 ? (
                <ul className="cooking-steps__autocomplete-list">
                    {options.map((option) => (
                        <li key={option.productCode}>
                            <button
                                type="button"
                                className="cooking-steps__autocomplete-option flex w-full cursor-pointer flex-col border-0 bg-transparent px-16 py-12 text-left hover:text-list-hover"
                                onClick={() => {
                                    setQuery(option.productCode);
                                    onChange(option.productCode);
                                    setOpen(false);
                                }}
                            >
                                <span className="cooking-steps__autocomplete-code">{option.productCode}</span>
                                <span className="cooking-steps__autocomplete-title">{option.title}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
};

export default ProductCodeAutocomplete;
